/**
 * TCS — quantity stepper focus continuity (A11Y-LA-03, R-032, as amended by
 * R-032a (target), R-032b (scheduling invariant, accepted cart limitation) and
 * R-032c (engine explanation withdrawn; keyboard-only claim corrected)).
 *
 * WHY THIS EXISTS
 * Horizon's QuantitySelectorComponent.updateButtonStates() writes
 * `minusButton.disabled = value <= min` and
 * `plusButton.disabled = effectiveMax !== null && value >= effectiveMax`,
 * synchronously with the visitor's own keypress. The stepper the shopper just
 * pressed is disabled WHILE IT HOLDS FOCUS, so the browser's focus fixup drops
 * focus to <body>: decrease a line to its minimum, or increase it to a finite
 * max, and a keyboard or screen-reader shopper loses their place in the cart
 * drawer, on /cart and on the PDP. WCAG 2.4.3 Focus Order (A).
 *
 * Evidence: docs/accessibility/evidence/2026-09-18/lane-a/A02.md (A02-5) and
 * A01.md (A01-1e); this implementation's measurements are in
 * docs/accessibility/evidence/2026-09-21/lane-a/la03.md.
 *
 * WHERE FOCUS GOES, AND WHY NOT SOMEWHERE MORE OBVIOUS
 * To the quantity selector's own CONTAINER — the `quantity-selector-component`
 * / `cart-quantity-selector-component` element — made focusable with a
 * `tabindex="-1"` set at the moment of the move and removed on its next
 * `focusout`. R-032a withdrew the two obvious targets after the #266 review
 * MEASURED what they do, and both failures share one cause: THE KEY THAT
 * CAUSED THE DISABLE IS STILL DOWN, so any activatable landing gets activated.
 *
 * - The counterpart stepper REVERSES THE SHOPPER'S INTENT. Line at 3, five
 *   Enter presses on Decrease: 2, 1, then focus lands on Increase and the next
 *   three presses run 2, 3, 4 — one debounced /cart/change commits quantity 4.
 *   A shopper pressing "decrease" repeatedly ends up with MORE of the item.
 *   Identical in Chromium 153 and WebKit 26.6, at a 180 ms cadence and under
 *   Chrome's Enter auto-repeat. Mitigations were refused: "don't move when the
 *   counterpart is the direction being repeated" is always true (the
 *   counterpart is by construction the reverse of the press that disabled the
 *   button), and a suppression window needs to swallow an activation on a
 *   vendored control — the stopPropagation pattern R-024 rejected.
 * - The quantity input SUBMITS THE FORM IT SITS IN. It is a form control, so
 *   Enter can trigger implicit submission. `#cart-form` (snippets/cart-products
 *   .liquid, method="post" action="{{ routes.cart_url }}") has no submit
 *   button, so implicit submission fires whenever exactly one blocking field is
 *   present — a SINGLE-LINE CART, which is precisely the state decreasing
 *   produces — and navigates the page away from the drawer. On the PDP the
 *   selector sits in the product form whose Add to cart is type="submit", so
 *   Enter ADDS TO CART, repeatedly under auto-repeat.
 *
 * The container is not a form control (no implicit submission), not activatable
 * (auto-repeat is inert on it), opens no soft keyboard on Chrome Android, and
 * sends no additional /cart/change. Tab continues from that point in the line
 * and Shift+Tab returns to the control before it — the 2.4.3 continuity the
 * finding asks for.
 *
 * STANDING RULE (R-032a): a focus-recovery target in this theme must never be
 * an element whose activation mutates the cart, submits a form, or navigates.
 *
 * ARMED BY KEYBOARD FOCUS — and what that does NOT mean (R-032c §4)
 * The move is ARMED ONLY BY KEYBOARD FOCUS: at `focusin` on the button, two
 * signals must agree — `:focus-visible` on the button at focus time, and a
 * document-level modality flag set on `keydown` and cleared on `pointerdown`.
 * It is never inferred inside the mutation callback, where focus may already
 * have moved.
 *
 * It is NOT true that pointer activation never moves focus. `armedButton` is set
 * at `focusin` and never re-evaluated, and a click on an already-focused button
 * produces no new `focusin` — so a stepper that was focused BY KEYBOARD and is
 * then activated BY MOUSE still moves focus in Chromium. WebKit cancels, because
 * its click focuses the drawer dialog and the `focusout` therefore carries a
 * non-null `relatedTarget`. Measured in both engines; pinned by the
 * `la03-pointer-divergence` scenario.
 *
 * This divergence is KNOWN AND ACCEPTED, not an oversight. R-032c corrected the
 * wording rather than the code: a `pointerdown` disarm would be a fourth guard
 * on a mechanism whose guard count has already produced three bugs, and moving
 * focus to a non-activatable container after a deliberate click is benign. A
 * COLD pointer click — on a stepper not already keyboard-focused — moves focus
 * on neither engine.
 *
 * THE SCHEDULING INVARIANT (R-032b) AND THE GUARDS AT ITS BOUNDARY
 *
 * The invariant, and the whole of it:
 *
 *   the move is EVALUATED AGAINST AN OBSERVED `document.activeElement`, never a
 *   predicted one, and no behaviour derives from elapsed time.
 *
 * So the mutation callback only ARMS; the `focusout` only SCHEDULES; and the
 * move itself is decided at a task boundary, where every condition is re-read.
 * The primitive must be a TASK, never an animation frame: a frame callback can
 * be withheld indefinitely in a background tab and would deliver a stale focus
 * move when the tab is restored.
 *
 * NO CAUSAL CLAIM IS MADE HERE ABOUT ENGINE ORDERING. An earlier version of this
 * header asserted that `focus()` during the `focusout` dispatch is undone and
 * that `queueMicrotask` cannot help. That did not reproduce: measured in
 * Chromium 153 and WebKit 26.6, a synchronous, microtask, task or
 * mutation-callback move all land on the container AND STAY. R-032c withdrew the
 * explanation. The task hop is therefore CORRECT BUT NOT NECESSARY, and
 * simplifying it is deliberately sequenced after the AT utterance (R-032c §3),
 * because R-032a's stop rule may withdraw this work entirely. The repro lives at
 * docs/accessibility/evidence/2026-09-21/lane-a/probes/la03-focus-timing.mjs —
 * re-run it before changing the scheduling.
 *
 * Every guard is evaluated AT the boundary, in move(), and a pending move that
 * fails any of them is ABANDONED, never retried:
 *   1. no `focusin` on another element has intervened;
 *   2. the recorded button is still the one that was keyboard-focused;
 *   3. that button is still connected, and still disabled;
 *   4. `document.activeElement` is `body` or null;
 *   5. the container is still connected and still in this document.
 * A `focusout` carrying a non-null `relatedTarget` cancels earlier, at the event,
 * because `relatedTarget` exists only there. No delay is tuned and nothing is
 * inferred from duration — that heuristic is what R-032a refused.
 *
 * A DETACHED button is not our business either — that is R-028's cart-morph
 * path, where component-cart-items.js owns focus continuation after a removal.
 *
 * ACCEPTED LIMITATION ON CART SURFACES (R-032b §2) — NOT A COMPLETED FIX
 * The container is focusable only because this script gives it a tabindex, and
 * Horizon's section morph strips attributes absent from the server markup. On
 * the PDP there is no morph on a quantity change, so the restoration is
 * permanent. On the cart drawer and /cart, focus is held FOR THE LIFE OF THE
 * REQUEST and returns to <body> when the morph lands: a keyboard or
 * screen-reader user who decreases a line to its minimum regains their place
 * only until the cart response is applied, after which the next Tab restarts
 * from the top of the drawer — origin/main's behaviour, reached a few hundred
 * milliseconds later. Not a regression, and neither withdrawn target's defect is
 * present. Pinned by `la03-cart-morph-drops-the-restored-focus` so it cannot
 * drift silently.
 *
 * THE REPAIR IS NOT GRANTED HERE. Re-focusing after a morph means telling focus
 * lost to the morph apart from focus the visitor moved during the request — the
 * logic R-028 already carries for the removal path — so it belongs in
 * assets/component-cart-items.js, which is carrying R-028b with R-028c queued.
 * If the AT result makes it necessary it returns as an R-028 amendment AFTER
 * R-028c lands. Do not add it here.
 *
 * COUPLING NOTE: assets/component-quantity-selector.js, Horizon 4.1.3, baseline
 * 37e2bd5e160d762f (assets/component-cart-quantity-selector.js, baseline
 * 8a39d2520733c86d, subclasses it and overrides updateButtonStates()). Neither
 * file is owned; both stay pristine. Relies on:
 * - the custom element names `quantity-selector-component` and
 *   `cart-quantity-selector-component`;
 * - `ref="minusButton"` and `ref="plusButton"` inside them
 *   (snippets/quantity-selector.liquid, and the cart row markup in
 *   snippets/cart-products.liquid);
 * - updateButtonStates() disabling those buttons through the `disabled` IDL
 *   attribute, which reflects to the content attribute the observer watches. An
 *   upgrade that made the control unavailable some other way (a class, `inert`,
 *   re-rendering the button) would produce no mutation record, and the focus
 *   loss would return with every markup assertion still green.
 * Tripwire: scripts/tests/tcs-quantity-focus-continuity-coupling.test.mjs.
 * Ring: assets/tcs-quantity-focus-continuity.css.
 * Browser regression: the `la03-*` scenarios in
 * scripts/tests/tcs-cart-line-focus.test.mjs.
 *
 * Progressive enhancement: if this file fails to load, Horizon behaves exactly
 * as it does today — the disable still happens, only the focus repair is lost.
 */
(() => {
  'use strict';

  const CONTAINERS = 'quantity-selector-component, cart-quantity-selector-component';
  const MINUS = '[ref="minusButton"]';
  const PLUS = '[ref="plusButton"]';

  /** True while the last input modality seen was a key, false after a pointer. */
  let keyboardModality = false;
  /** The stepper button focused by keyboard, or null. Set only at focusin. */
  let armedButton = null;
  /** The button whose disable we are waiting to see focus fall away from. */
  let pendingButton = null;
  /** A container we gave a temporary tabindex, to be removed on its focusout. */
  let temporaryHost = null;
  /** One task hop is scheduled at a time; the guards themselves stay live. */
  let scheduled = false;

  document.addEventListener(
    'keydown',
    () => {
      keyboardModality = true;
    },
    true,
  );
  document.addEventListener(
    'pointerdown',
    () => {
      keyboardModality = false;
    },
    true,
  );

  const isStepper = (node) =>
    node instanceof HTMLButtonElement &&
    (node.matches(MINUS) || node.matches(PLUS)) &&
    node.closest(CONTAINERS) !== null;

  /** :focus-visible is unsupported nowhere we ship, but never let it throw. */
  function focusVisible(element) {
    try {
      return element.matches(':focus-visible');
    } catch {
      return false;
    }
  }

  /**
   * Give back the temporary tabindex.
   *
   * REFACTOR CONSTRAINT, measured 2026-09-21 and pinned by nothing else:
   * removing `tabindex` from a container that STILL HOLDS FOCUS loses that focus
   * to <body> in BOTH engines. Chromium blurs synchronously, inside
   * removeAttribute(). WebKit blurs asynchronously with VARIABLE LATENCY —
   * measured still holding the container immediately after the call, at the next
   * task, at +100 ms, and in one run all the way through a 400 ms settle, while
   * six of six runs had blurred by 900 ms.
   *
   * That variability is the hazard: A TEST OR SPOT-CHECK IN WEBKIT CAN PASS AND
   * THE BUG STILL BE THERE. Do not conclude from a green WebKit run that a
   * release site is safe.
   *
   * Both call sites are safe today and must stay that way: the `focusout`
   * listener runs when focus has already left the host, and move() calls this
   * only after guard 4 has just observed `activeElement` to be `body`. Do not
   * call it anywhere focus might still be on the host.
   */
  function releaseHost() {
    if (!temporaryHost) return;
    temporaryHost.removeAttribute('tabindex');
    temporaryHost = null;
  }

  document.addEventListener(
    'focusin',
    (event) => {
      const target = event.target;
      // Any focus landing on something other than the pending button cancels a
      // move armed for it: the visitor, or another script, moved focus first.
      if (pendingButton && target !== pendingButton) pendingButton = null;
      armedButton = isStepper(target) && keyboardModality && focusVisible(target) ? target : null;
    },
    true,
  );

  document.addEventListener(
    'focusout',
    (event) => {
      const target = event.target;
      if (temporaryHost && target === temporaryHost) releaseHost();
      if (!pendingButton || target !== pendingButton) return;

      const button = pendingButton;

      // A deliberate move: the visitor, or another script, named where focus
      // goes next. Never take it back.
      if (event.relatedTarget !== null) {
        pendingButton = null;
        return;
      }

      // pendingButton is deliberately LEFT SET until the boundary, so that an
      // intervening focusin between here and there still cancels (R-032b). Only
      // the scheduling is one-shot.
      if (scheduled) return;
      scheduled = true;

      // Defer to a task boundary, where every guard is re-read against an
      // OBSERVED activeElement (R-032b's invariant). It must be a TASK, not an
      // animation frame: a frame callback can be withheld indefinitely in a
      // background tab and would deliver a stale focus move on return.
      // This hop is correct but NOT necessary — a synchronous move also works in
      // both engines (R-032c; see the header and the probe). Simplifying it is
      // sequenced after the AT utterance, not now.
      setTimeout(() => {
        scheduled = false;
        move(button);
      }, 0);
    },
    true,
  );

  /**
   * Complete an armed move. EVERY R-032a guard is evaluated here, at the task
   * boundary — not earlier — and a move that fails any of them is ABANDONED,
   * never retried (R-032b).
   * @param {HTMLButtonElement} button
   */
  function move(button) {
    // 1. No focusin on another element has intervened. pendingButton is cleared
    //    by the focusin handler, so a cancel between the blur and here lands.
    if (pendingButton !== button) return;
    pendingButton = null;
    // 2. Still the button that was keyboard-focused. armedButton is re-pointed
    //    by any later focusin, so this also catches focus that came and went.
    if (armedButton !== button) return;
    // 3. Still connected, and still the disabled control we armed for.
    if (!button.isConnected || !button.disabled) return;
    // 4. The fixup has run by now: focus must have landed nowhere. Read, not
    //    predicted. If anything else holds it, that is where the visitor is.
    const active = document.activeElement;
    if (active && active !== document.body) return;
    // 5. The container is still connected AND still in this document.
    const host = button.closest(CONTAINERS);
    if (!host || !host.isConnected || host.ownerDocument !== document) return;

    releaseHost();
    if (!host.hasAttribute('tabindex')) {
      host.setAttribute('tabindex', '-1');
      temporaryHost = host;
    }
    host.focus();
  }

  const observer = new MutationObserver((records) => {
    for (const record of records) {
      const button = record.target;
      if (button !== armedButton) continue;
      if (!(button instanceof HTMLButtonElement) || !button.disabled || !button.isConnected)
        continue;
      // Arm only. The engines have not run their focus fixup yet (measured in
      // both), so the move completes at this button's own focusout.
      pendingButton = button;
      return;
    }
  });

  observer.observe(document.documentElement, {
    subtree: true,
    attributes: true,
    attributeFilter: ['disabled'],
  });
})();
